/**
 * onboardingStore.ts
 * First-run product tour progress (React Joyride). Client-only, no PII.
 */
const STORAGE_KEY = "tvs-excel-uploader:onboarding";
export const ONBOARDING_VERSION = 1 as const;

export type TourPhase = "data" | "preview";

export interface OnboardingState {
  version: typeof ONBOARDING_VERSION;
  completed: boolean;
  completedAt?: number;
  skipped?: boolean;
  phasesDone: { data?: boolean; preview?: boolean };
}

const DEFAULTS: OnboardingState = {
  version: ONBOARDING_VERSION,
  completed: false,
  phasesDone: {},
};

export function loadOnboarding(): OnboardingState {
  if (typeof localStorage === "undefined") return { ...DEFAULTS, phasesDone: {} };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS, phasesDone: {} };
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    // Major UX bump → show tour again.
    if (parsed.version !== ONBOARDING_VERSION) {
      return { ...DEFAULTS, phasesDone: {} };
    }
    return {
      version: ONBOARDING_VERSION,
      completed: Boolean(parsed.completed),
      completedAt: typeof parsed.completedAt === "number" ? parsed.completedAt : undefined,
      skipped: Boolean(parsed.skipped),
      phasesDone: {
        data: Boolean(parsed.phasesDone?.data),
        preview: Boolean(parsed.phasesDone?.preview),
      },
    };
  } catch {
    return { ...DEFAULTS, phasesDone: {} };
  }
}

export function saveOnboarding(patch: Partial<OnboardingState>): OnboardingState {
  const prev = loadOnboarding();
  const next: OnboardingState = {
    version: ONBOARDING_VERSION,
    completed: patch.completed ?? prev.completed,
    completedAt: patch.completedAt ?? prev.completedAt,
    skipped: patch.skipped ?? prev.skipped,
    phasesDone: {
      ...prev.phasesDone,
      ...(patch.phasesDone ?? {}),
    },
  };
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore quota
    }
  }
  return next;
}

export function markPhaseDone(phase: TourPhase): OnboardingState {
  const phasesDone = { ...loadOnboarding().phasesDone, [phase]: true };
  const both = Boolean(phasesDone.data && phasesDone.preview);
  return saveOnboarding({
    phasesDone,
    completed: both ? true : loadOnboarding().completed,
    completedAt: both ? Date.now() : undefined,
  });
}

export function markTourSkipped(): OnboardingState {
  return saveOnboarding({
    completed: true,
    skipped: true,
    completedAt: Date.now(),
    phasesDone: { data: true, preview: true },
  });
}

export function markTourCompleted(): OnboardingState {
  return saveOnboarding({
    completed: true,
    skipped: false,
    completedAt: Date.now(),
    phasesDone: { data: true, preview: true },
  });
}

export function resetOnboarding(): OnboardingState {
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
  return { ...DEFAULTS, phasesDone: {} };
}

export function shouldAutoStartDataTour(state: OnboardingState = loadOnboarding()): boolean {
  if (state.completed) return false;
  return !state.phasesDone.data;
}

export function shouldAutoStartPreviewTour(state: OnboardingState = loadOnboarding()): boolean {
  if (state.completed) return false;
  return Boolean(state.phasesDone.data) && !state.phasesDone.preview;
}

export const ONBOARDING_STORAGE_KEY = STORAGE_KEY;
